/* <video-carousel>: an endless row of videos. The centre slide is the only one that plays (muted, looping);
   the others are paused, smaller and dimmed. Moves with the arrows, by swiping/dragging, and on a timer.

   Endless loop: copies of the first and last slides are added on both sides, and after every move the track
   silently jumps back to the real slides when it has reached a copy. */
const CLONES = 4;
const MOVE_MS = 600;
const RESUME_MS = 2000;
const SWIPE_PX = 50;

class VideoCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.viewport = this.querySelector('[data-viewport]');
    const originals = [...this.querySelectorAll('[data-slide]')];
    if (!this.track || !originals.length) return;

    this.abort = new AbortController();
    this.originalCount = originals.length;
    this.autoScroll = this.dataset.autoScroll === 'true' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.delay = Math.max(1000, Number(this.dataset.autoScrollDelay) || 7000);
    this.visible = false;
    this.near = false;
    this.moving = false;

    this.addClones(originals);
    this.slides = [...this.track.querySelectorAll('[data-slide]')];
    this.videos = this.slides.map((slide) => slide.querySelector('[data-video]'));
    this.videos.forEach((video) => { if (video) { video.muted = true; video.defaultMuted = true; } });
    this.index = CLONES;

    this.bindEvents();
    this.update(true);
    requestAnimationFrame(() => this.update(true));
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.stopTimer();
    clearTimeout(this.resumeTimer);
    clearTimeout(this.moveTimer);
    this.observer?.disconnect();
    this.nearObserver?.disconnect();
    this.resizer?.disconnect();
    this.track?.querySelectorAll('[data-clone]').forEach((clone) => clone.remove());
    this.slides = null;
  }

  addClones(originals) {
    const n = originals.length;
    const make = (slide) => {
      const clone = slide.cloneNode(true);
      clone.setAttribute('data-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      return clone;
    };
    for (let i = 1; i <= CLONES; i++) this.track.prepend(make(originals[(((n - i) % n) + n) % n]));
    for (let i = 0; i < CLONES; i++) this.track.append(make(originals[i % n]));
  }

  bindEvents() {
    const { signal } = this.abort;
    this.querySelector('[data-nav="prev"]')?.addEventListener('click', () => this.userMove(-1), { signal });
    this.querySelector('[data-nav="next"]')?.addEventListener('click', () => this.userMove(1), { signal });

    // Swipe or drag. The viewport allows vertical page scrolling (touch-action: pan-y), so a horizontal
    // gesture reaches us and a vertical one scrolls the page as usual.
    let startX = null;
    this.viewport.addEventListener('pointerdown', (e) => { startX = e.clientX; this.pauseAuto(); }, { signal });
    const end = (e) => {
      if (startX === null) return;
      const dx = e.clientX - startX;
      startX = null;
      if (e.type === 'pointerup' && Math.abs(dx) > SWIPE_PX) this.move(dx < 0 ? 1 : -1);
      this.scheduleResume();
    };
    this.viewport.addEventListener('pointerup', end, { signal });
    this.viewport.addEventListener('pointercancel', end, { signal });

    // Keep the centre slide centred when the width changes (also covers styles that arrive late).
    let resizeTimer;
    this.resizer = new ResizeObserver(() => { clearTimeout(resizeTimer); resizeTimer = setTimeout(() => this.update(true), 100); });
    this.resizer.observe(this.viewport);

    // Start loading videos shortly before the block is on screen.
    this.nearObserver = new IntersectionObserver(([entry]) => {
      this.near = entry.isIntersecting;
      if (this.near) this.preload();
    }, { rootMargin: '400px 0px' });
    this.nearObserver.observe(this);

    // Play when the block is on screen; pause everything when it leaves.
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) { this.playActive(); this.startTimer(); } else { this.pauseAll(); this.stopTimer(); }
    }, { threshold: 0.2 });
    this.observer.observe(this);

    // Strict mobile browsers only allow playback after a first touch or scroll.
    const unlock = () => this.playActive();
    document.addEventListener('touchstart', unlock, { once: true, passive: true, signal });
    document.addEventListener('scroll', unlock, { once: true, passive: true, signal });
  }

  /* ----- movement ----- */
  userMove(step) {
    this.pauseAuto();
    this.move(step);
    this.scheduleResume();
  }

  move(step) {
    if (this.moving || !this.slides) return;
    this.moving = true;
    this.index += step;
    this.update(false);
    clearTimeout(this.moveTimer);
    this.moveTimer = setTimeout(() => {
      this.wrap();
      this.moving = false;
    }, MOVE_MS);
  }

  // Jump from a copy back to the matching real slide without any animation.
  wrap() {
    const n = this.originalCount;
    if (this.index >= CLONES + n) this.index -= n;
    else if (this.index < CLONES) this.index += n;
    else return;
    this.update(true);
  }

  update(instant) {
    if (!this.slides) return;
    const slide = this.slides[this.index];
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const offset = this.index * (slide.offsetWidth + gap) - this.viewport.clientWidth / 2 + slide.offsetWidth / 2;
    if (instant) this.track.classList.add('is-jumping');
    this.track.style.transform = `translateX(${-offset}px)`;
    this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
    if (instant) {
      void this.track.offsetWidth; // apply the jump before transitions come back
      this.track.classList.remove('is-jumping');
    }
    this.videos.forEach((video, i) => { if (video && i !== this.index) video.pause(); });
    this.preload();
    if (this.visible) this.playActive();
  }

  // Only the centre slide and its neighbours are loaded.
  preload() {
    if (!this.near || !this.videos) return;
    this.videos.forEach((video, i) => {
      if (video && Math.abs(i - this.index) <= 2 && video.preload !== 'auto') { video.preload = 'auto'; video.load(); }
    });
  }

  /* ----- playback ----- */
  playActive() {
    const video = this.videos?.[this.index];
    if (!video) return;
    const start = () => {
      if (video !== this.videos[this.index]) return;
      video.play().catch(() => setTimeout(() => { if (video === this.videos[this.index]) video.play().catch(() => {}); }, 150));
    };
    if (video.readyState >= 2) start();
    else video.addEventListener('loadeddata', start, { once: true });
  }

  pauseAll() {
    this.videos?.forEach((video) => video?.pause());
  }

  /* ----- timer ----- */
  startTimer() {
    if (!this.autoScroll || this.timer || this.paused) return;
    this.timer = setInterval(() => this.move(1), this.delay);
  }

  stopTimer() {
    clearInterval(this.timer);
    this.timer = null;
  }

  pauseAuto() {
    this.paused = true;
    this.stopTimer();
    clearTimeout(this.resumeTimer);
  }

  scheduleResume() {
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(() => {
      this.paused = false;
      if (this.visible) this.startTimer();
    }, RESUME_MS);
  }
}

if (!customElements.get('video-carousel')) customElements.define('video-carousel', VideoCarousel);
